import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { dashboardApi, investorApi, analysisApi, subscriptionApi } from '@/services/api'
import { useAuth } from '@/contexts/AuthContext'
import { useEffect } from 'react';
import { Link } from 'react-router-dom'
import {
  Search,
  LayoutGrid,
  Briefcase,
  TrendingUp,
  CreditCard,
  Plus,
  Bell,
  Menu,
  Zap,
  Calendar,
  Eye,
  BrainCircuit,
  Leaf,
  Rocket,
  ShoppingBag,
  Landmark,
  ArrowRight,
  Database,
  MapPin,
  Sparkles, Newspaper, Clock, ExternalLink,
  FileText,
  RefreshCw,
  Building2,
  CalendarDays,
  Globe,
  CheckCircle,
  AlertTriangle,
  Users,
  Target,
  Package,
  Shield
} from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

export function DashboardPage() {
  const { user } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStartup, setSelectedStartup] = useState<any>(null)
  const queryClient = useQueryClient()

  // Retry failed analysis mutation (with optimistic UI update)
  const retryMutation = useMutation({
    mutationFn: (startupId: string) => analysisApi.retryAnalysis(startupId),
    onMutate: async (startupId) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['my-analyses'] })

      // Snapshot the previous value
      const previous = queryClient.getQueryData(['my-analyses'])

      // Optimistically update the status to 'running'
      queryClient.setQueryData(['my-analyses'], (old: any[] | undefined) =>
        old?.map((item: any) =>
          item.startup?.id === startupId ? { ...item, status: 'running' } : item
        )
      )

      return { previous }
    },
    onError: (_err, _startupId, context) => {
      // Roll back to previous value on error
      if (context?.previous) {
        queryClient.setQueryData(['my-analyses'], context.previous)
      }
    },
  })

  // -- Queries --

  const { data: myAnalyses, isLoading: loadingAnalyses } = useQuery({
    queryKey: ['my-analyses'],
    queryFn: dashboardApi.getMyAnalyses,
  })

  const { data: subscription } = useQuery({
    queryKey: ['subscription-current'],
    queryFn: subscriptionApi.getCurrent,
  })

  const { data: emergingVentures, isLoading: loadingVentures } = useQuery({
    queryKey: ['emerging-ventures'],
    queryFn: dashboardApi.getEmergingVentures,
  })


  const { data: highlightedStartups, isLoading: loadingHighlighted } = useQuery({
    queryKey: ['highlighted-startups'],
    queryFn: dashboardApi.getHighlightedStartups,
  })

  const { data: startupNews, isLoading: loadingNews } = useQuery({
    queryKey: ['startup-news'],
    queryFn: dashboardApi.getStartupNews,
  })

  // -- Helpers for Theme & Status --

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-emerald-500/10 text-emerald-500 border-emerald-200/20'
      case 'running': return 'bg-blue-500/10 text-blue-500 border-blue-200/20'
      case 'pending':
      case 'processing': return 'bg-yellow-500/10 text-yellow-500 border-yellow-200/20'
      case 'failed':
      case 'error':
      case 'action required': return 'bg-red-500/10 text-red-500 border-red-200/20'
      default: return 'bg-slate-500/10 text-slate-500 border-slate-200/20'
    }
  }

  const getScoreColor = (score: number) => {
    if (!score) return 'text-slate-400'
    if (score >= 8) return 'text-emerald-500'
    if (score >= 6) return 'text-yellow-500'
    return 'text-red-500'
  }

  // Helper icons mapping based on industry or random fallback
  const getIndustryIcon = (industry: string) => {
    const i = industry?.toLowerCase() || ''
    if (i.includes('energy') || i.includes('clean') || i.includes('tech')) return <Zap className="w-5 h-5" />
    if (i.includes('health') || i.includes('bio') || i.includes('med')) return <BrainCircuit className="w-5 h-5" />
    if (i.includes('agri') || i.includes('food')) return <Leaf className="w-5 h-5" />
    if (i.includes('finance') || i.includes('fintech')) return <Landmark className="w-5 h-5" />
    if (i.includes('commerce') || i.includes('retail')) return <ShoppingBag className="w-5 h-5" />
    return <Rocket className="w-5 h-5" />
  }

  const getIndustryColor = (industry: string) => {
    const i = industry?.toLowerCase() || ''
    if (i.includes('energy') || i.includes('clean')) return 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
    if (i.includes('health')) return 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
    if (i.includes('agri')) return 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
    if (i.includes('finance')) return 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400'
    if (i.includes('commerce')) return 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400'
    return 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
  }

  return (
    <div className="flex flex-col h-[calc(100vh-112px)] overflow-hidden bg-background-light dark:bg-background-dark font-sans text-slate-900 dark:text-slate-100 transition-colors duration-300">

      {/* MAIN CONTENT */}
      <main className="flex-1 overflow-hidden bg-slate-50/50 dark:bg-background-dark flex flex-col">
        <div className="pt-0 md:pt-0 px-4 md:px-6 xl:px-8 pb-6 md:pb-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-4 xl:gap-6 flex-1 overflow-hidden">

          
          {/* LEFT SIDEBAR */}
          <div className="lg:col-span-4 xl:col-span-3 flex flex-col min-h-0">

            {/* USAGE BANNER */}
            {subscription?.usage && subscription.plan_name !== 'Enterprise' && (
              <div className="mb-6 p-4 rounded-xl border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-slate-900 dark:text-white">Analysis Credits</h3>
                  <p className="text-sm text-slate-500">
                    {subscription.usage.limit === -1 ? (
                      'Unlimited usage'
                    ) : (
                      <>
                        <span className={subscription.usage.limit - subscription.usage.used <= 1 ? 'text-red-500 font-bold' : ''}>
                          {subscription.usage.limit - subscription.usage.used}
                        </span> left this month
                      </>
                    )}
                  </p>
                </div>
                {subscription.usage.limit !== -1 && (
                  <Link to="/plans">
                    <Button size="sm" variant="outline" className="font-bold">
                      Upgrade
                    </Button>
                  </Link>
                )}
              </div>
            )}

            {/* SECTION: Highlighted Startups */}
            <section className="animate-in fade-in slide-in-from-bottom-8 duration-1000 flex flex-col flex-1 min-h-0">
              <div className="flex items-center gap-2 mb-6 shrink-0 xl:h-[64px]">
                <Sparkles className="w-6 h-6 text-amber-500 shrink-0" />
                <h2 className="text-xl md:text-2xl font-display font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300 bg-clip-text text-transparent">Highlighted Startups</h2>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm flex flex-col flex-1 min-h-0">
                <div className="overflow-y-auto scrollbar-slim flex-1 p-4 space-y-4">
                  {loadingHighlighted ? (
                    <div className="flex justify-center items-center h-40">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : highlightedStartups?.length === 0 ? (
                    <div className="text-center p-8 text-slate-500">No highlighted startups right now.</div>
                  ) : (
                    highlightedStartups?.map((startup: any) => (
                      <div 
                        key={startup.id} 
                        onClick={() => setSelectedStartup(startup)}
                        className="p-4 rounded-xl border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-primary/50 hover:shadow-md hover:bg-primary/5 transition-all cursor-pointer flex gap-4 items-center group"
                      >
                         <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center p-1 overflow-hidden shrink-0">
                            {startup.logo_url ? (
                              <img src={startup.logo_url} alt={startup.name} className="w-full h-full object-contain" />
                            ) : (
                              <Rocket className="w-6 h-6 text-primary" />
                            )}
                         </div>
                         <div className="flex-1 min-w-0 space-y-0.5">
                           <h3 className="font-bold text-slate-900 dark:text-white text-sm group-hover:text-primary transition-colors leading-tight">{startup.name}</h3>
                           <p className="text-xs text-slate-500">{startup.industry || 'Technology'}</p>
                         </div>
                         <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-primary transition-colors" />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </section>
          </div>
          
          {/* MAIN COLUMN */}
          <div className="lg:col-span-8 xl:col-span-6 flex flex-col min-h-0">

            {/* SECTION 1: Active Portfolio Analyses */}
          <section className="animate-in fade-in slide-in-from-bottom-4 duration-700 flex flex-col flex-1 min-h-0">
            <div className="flex flex-col justify-center mb-6 shrink-0 xl:h-[64px]">
              <h2 className="text-2xl md:text-3xl font-display font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300 bg-clip-text text-transparent mb-1">Private Analysis</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Track AI-driven evaluations of your strategic startup investments.</p>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-slim pr-2 pb-2 flex flex-col gap-4">
              <div className="flex gap-3 shrink-0">
                <div className="relative flex-1 sm:max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input
                    className="pl-9 pr-4 py-2.5 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none w-full text-slate-900 dark:text-white placeholder-slate-400 transition-all shadow-sm"
                    placeholder="Search ventures..."
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Link to="/investor-submission" className="shrink-0">
                  <Button className="flex items-center gap-2 bg-logo-gradient hover:opacity-90 text-white px-4 py-2.5 rounded-xl text-sm font-bold transition-all shadow-md shadow-primary/20 border-none h-[42px]">
                    <Plus className="w-4 h-4" />
                    New Evaluation
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-1 gap-4">
              {loadingAnalyses ? (
                // Loading Skeletons
                [1, 2].map(i => (
                  <div key={i} className="h-32 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 animate-pulse" />
                ))
              ) : myAnalyses?.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center p-16 bg-white dark:bg-slate-800 rounded-xl border border-dashed border-slate-200 dark:border-slate-700 min-h-[300px]">
                  <div className="w-16 h-16 bg-indigo-50 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mb-4">
                    <Rocket className="w-8 h-8 text-indigo-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No Active Analyses Yet</h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md">
                    You haven't run any evaluations yet. Start analyzing startups to get AI-driven insights tailored to your investment thesis.
                  </p>
                  <Link to="/investor-submission">
                    <Button className="bg-logo-gradient text-white font-bold px-8 py-6 h-auto text-lg rounded-xl shadow-lg shadow-primary/20 hover:shadow-primary/30 hover:-translate-y-0.5 transition-all">
                      <Plus className="w-5 h-5 mr-2" />
                      Start your analysis journey
                    </Button>
                  </Link>
                </div>
              ) : (
                myAnalyses?.filter((item: any) =>
                  !searchQuery || item.startup?.name?.toLowerCase().includes(searchQuery.toLowerCase())
                ).map((item: any) => {
                  const status = item.status?.toLowerCase() || '';
                  const isCompleted = status === 'completed';
                  const isFailed = status === 'failed' || status === 'error';
                  const industryColor = getIndustryColor(item.startup?.industry || 'tech');

                  return (
                      <div key={item.id} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 sm:p-5 shadow-sm hover:shadow-md transition-all group relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 cursor-default">
                        <div className="flex items-start gap-4 flex-1 min-w-0 w-full">
                          <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl ${industryColor} flex items-center justify-center shrink-0 shadow-sm overflow-hidden bg-white p-1`}>
                            {item.startup?.logo_url ? (
                              <img src={item.startup.logo_url} alt={item.startup.name} className="w-full h-full object-contain" />
                            ) : (
                              getIndustryIcon(item.startup?.industry)
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="font-bold text-lg xl:text-xl text-slate-900 dark:text-white group-hover:text-primary transition-colors">
                                {item.startup?.name || 'Unknown Startup'}
                              </h3>
                              <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${getStatusColor(item.status)}`}>
                                {item.status}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-xs xl:text-sm text-slate-500 dark:text-slate-400 font-medium truncate mb-1">
                              <span>{item.startup?.industry ? capitalizeFirst(item.startup.industry) : 'Tech'} • {item.startup?.funding_stage ? capitalizeFirst(item.startup.funding_stage) : 'Seed'}</span>
                              <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600 shrink-0"></span>
                              <span className="flex items-center gap-1.5 shrink-0">
                                <Calendar className="w-3.5 h-3.5" />
                                {item.date ? new Date(item.date).toLocaleDateString() : 'Date N/A'}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2 leading-relaxed w-full">
                              {item.summary_text || item.startup?.description || "No description available."}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 shrink-0 w-full sm:w-auto mt-2 sm:mt-0 pt-3 sm:pt-0 border-t sm:border-0 border-slate-100 dark:border-slate-700">
                          {isCompleted && (
                            <div className="flex items-center gap-1.5 shrink-0 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg hidden xl:flex">
                              <TrendingUp className="w-4 h-4 text-emerald-500" />
                              <span className={`font-bold ${getScoreColor(item.score)} text-sm`}>Score: {item.score}/10</span>
                            </div>
                          )}
                          <div className="flex gap-2 w-full sm:w-auto ml-auto">
                            {isCompleted ? (
                              <Link to={`/startups/${item.startup?.id}/analysis`} className="w-full sm:w-auto flex-1 sm:flex-none">
                                <Button size="sm" className="w-full sm:w-auto h-9 px-4 rounded-lg font-bold bg-logo-gradient text-white border-none shadow-md shadow-primary/20 hover:opacity-90 whitespace-nowrap">
                                  Review Insights
                                </Button>
                              </Link>
                            ) : isFailed ? (
                              <Button
                                size="sm"
                                onClick={() => retryMutation.mutate(item.startup?.id)}
                                disabled={retryMutation.isPending && retryMutation.variables === item.startup?.id}
                                className="w-full sm:w-auto h-9 px-4 rounded-lg font-bold bg-amber-500 hover:bg-amber-600 text-white border-none shadow-md shadow-amber-500/20 transition-all flex-1 sm:flex-none whitespace-nowrap"
                              >
                                <RefreshCw className={`w-4 h-4 mr-2 shrink-0 ${retryMutation.isPending && retryMutation.variables === item.startup?.id ? 'animate-spin' : ''}`} />
                                {retryMutation.isPending && retryMutation.variables === item.startup?.id ? 'Retrying...' : 'Retry Analysis'}
                              </Button>
                            ) : (
                              <button disabled className="w-full sm:w-auto h-9 px-4 text-sm font-bold text-slate-400 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 rounded-lg cursor-not-allowed flex-1 sm:flex-none whitespace-nowrap">
                                Await Report
                              </button>
                            )}

                            <Link to={`/startups/${item.startup?.id}`} className="shrink-0">
                              <button className="px-3 py-2 text-slate-500 hover:text-primary border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors bg-white dark:bg-slate-800 shadow-sm h-9 flex items-center justify-center">
                                <Eye className="w-4 h-4" />
                              </button>
                            </Link>
                          </div>
                        </div>
                      </div>
                  );
                })
              )}
              </div>
            </div>
          </section>

          </div>

          {/* RIGHT SIDEBAR */}
          <div className="lg:col-span-12 xl:col-span-3 flex flex-col min-h-0">
{/* SECTION 3: Startup News */}
            <section className="animate-in fade-in slide-in-from-bottom-8 duration-1000 flex flex-col flex-1 min-h-0">
              <div className="flex items-center gap-2 mb-6 shrink-0 xl:h-[64px]">
                <Newspaper className="w-6 h-6 text-primary shrink-0" />
                <h2 className="text-xl md:text-2xl font-display font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300 bg-clip-text text-transparent">Latest Startup News</h2>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm flex flex-col flex-1 min-h-0">
                <div className="p-4 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                  <span className="text-sm font-bold text-slate-600 dark:text-slate-300">Live Feed</span>
                  <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800/50">Aggregated</Badge>
                </div>
                <div className="overflow-y-auto scrollbar-slim flex-1 p-4 space-y-4">
                  {loadingNews ? (
                    <div className="flex justify-center items-center h-40">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : startupNews?.length === 0 ? (
                    <div className="text-center p-8 text-slate-500">No news available.</div>
                  ) : (
                    startupNews?.map((news: any) => {
                      const pubDate = new Date(news.published_at);
                      const timeAgo = Math.floor((new Date().getTime() - pubDate.getTime()) / (1000 * 60 * 60));

                      const getFaviconUrl = (sourceName: string) => {
                        const domainMap: Record<string, string> = {
                          "TechCrunch": "techcrunch.com",
                          "Economic Times": "economictimes.indiatimes.com",
                          "YourStory": "yourstory.com"
                        };
                        const domain = domainMap[sourceName] || sourceName.toLowerCase().replace(/\s+/g, '') + '.com';
                        // Using DuckDuckGo's favicon service as a reliable alternative
                        return `https://icons.duckduckgo.com/ip3/${domain}.ico`;
                      };

                      return (
                        <a key={news.id} href={news.url} target="_blank" rel="noopener noreferrer" className="block group">
                          <div className="p-4 rounded-xl border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-primary/30 hover:shadow-md transition-all flex gap-4 items-start">
                            {news.image_url ? (
                              <div className="w-16 h-16 shrink-0 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700">
                                <img src={news.image_url} alt="Thumbnail" className="w-full h-full object-cover" />
                              </div>
                            ) : (
                              <div className="w-16 h-16 shrink-0 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-400">
                                <Newspaper className="w-8 h-8" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start gap-2 mb-1.5">
                                {news.source && (
                                  <img src={getFaviconUrl(news.source)} alt={news.source} className="w-4 h-4 mt-0.5 rounded object-contain bg-white shrink-0 p-0.5" />
                                )}
                                <h3 className="font-bold text-slate-900 dark:text-white text-sm leading-snug group-hover:text-primary transition-colors">
                                  {news.title}
                                </h3>
                              </div>
                              <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mb-3">
                                {news.description || news.summary}
                              </p>
                              <div className="flex items-center justify-between text-xs text-slate-400 font-medium">
                                <div className="flex items-center gap-1.5">
                                  <Clock className="w-3.5 h-3.5" />
                                  <span>{timeAgo < 24 ? `${timeAgo}h ago` : pubDate.toLocaleDateString()}</span>
                                </div>
                                <span className="flex items-center gap-1 group-hover:text-primary transition-colors">
                                  Read <ExternalLink className="w-3 h-3" />
                                </span>
                              </div>
                            </div>
                          </div>
                        </a>
                      )
                    })
                  )}
                </div>
              </div>
            </section>

          </div>
        </div>
      
      {/* MODAL FOR HIGHLIGHTED STARTUP */}
      <Dialog open={!!selectedStartup} onOpenChange={(open) => !open && setSelectedStartup(null)}>
        <DialogContent className="max-w-[80vw] w-[80vw] max-h-[80vh] overflow-y-auto border-none bg-slate-50 dark:bg-slate-900 shadow-2xl p-0">
          {/* Header Section */}
          <div className="bg-white dark:bg-slate-950 px-8 py-8 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-6">
                {selectedStartup?.logo_url ? (
                  <img src={selectedStartup?.logo_url} className="w-20 h-20 rounded-xl object-contain bg-white shadow-sm border border-slate-100" alt="Logo" />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shadow-sm">
                    <Building2 className="w-10 h-10 text-slate-400" />
                  </div>
                )}
                <div>
                  <DialogTitle className="text-4xl font-black font-display tracking-tight text-slate-900 dark:text-white mb-2">
                    {selectedStartup?.name}
                  </DialogTitle>
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-200 text-sm px-3 py-1">
                      {selectedStartup?.industry || 'Technology'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="px-8 py-8 space-y-10">
            <div>
              <p className="text-lg leading-relaxed text-slate-600 dark:text-slate-300">
                {selectedStartup?.short_description || selectedStartup?.description || 'No description available for this startup.'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" /> Founded
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg font-semibold text-slate-900 dark:text-white">
                    {selectedStartup?.founded_year || 'Unknown'}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> Stage
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg font-semibold text-slate-900 dark:text-white">
                    {selectedStartup?.funding_status || 'Unknown Stage'}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
                    <Globe className="w-4 h-4" /> Location
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg font-semibold text-slate-900 dark:text-white">
                    {selectedStartup?.location || 'Unknown Location'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Core Product & Target Audience */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {selectedStartup?.core_product_or_service && (
                <Card className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                      <Package className="w-5 h-5 text-indigo-500" /> Core Product / Service
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 dark:text-slate-300">{selectedStartup.core_product_or_service}</p>
                  </CardContent>
                </Card>
              )}
              {selectedStartup?.target_audience && (
                <Card className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                      <Target className="w-5 h-5 text-blue-500" /> Target Audience
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 dark:text-slate-300">{selectedStartup.target_audience}</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Green & Red Flags */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {selectedStartup?.green_flags && selectedStartup.green_flags.length > 0 && (
                <Card className="bg-green-50/50 dark:bg-green-950/20 border-green-200 dark:border-green-900">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
                      <CheckCircle className="w-5 h-5" /> Strengths (Green Flags)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {selectedStartup.green_flags.map((flag: string, i: number) => (
                        <li key={i} className="flex gap-2 text-slate-700 dark:text-slate-300">
                          <span className="text-green-500 font-bold">•</span> 
                          <span>{flag}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
              {selectedStartup?.red_flags && selectedStartup.red_flags.length > 0 && (
                <Card className="bg-red-50/50 dark:bg-red-950/20 border-red-200 dark:border-red-900">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                      <AlertTriangle className="w-5 h-5" /> Risks (Red Flags)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {selectedStartup.red_flags.map((flag: string, i: number) => (
                        <li key={i} className="flex gap-2 text-slate-700 dark:text-slate-300">
                          <span className="text-red-500 font-bold">•</span> 
                          <span>{flag}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Other details: Competitors, Team, Lookout */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {selectedStartup?.key_competitors && selectedStartup.key_competitors.length > 0 && (
                <Card className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                      <Shield className="w-5 h-5 text-orange-500" /> Key Competitors
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {selectedStartup.key_competitors.map((comp: string, i: number) => (
                        <Badge key={i} variant="outline" className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                          {comp}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {selectedStartup?.team_details && selectedStartup.team_details.length > 0 && (
                <Card className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                      <Users className="w-5 h-5 text-emerald-500" /> Team Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {selectedStartup.team_details.map((member: string, i: number) => (
                        <li key={i} className="text-slate-600 dark:text-slate-300 text-sm flex gap-2">
                          <span className="text-emerald-500">•</span> {member}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {selectedStartup?.lookout_for && selectedStartup.lookout_for.length > 0 && (
                <Card className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                      <Eye className="w-5 h-5 text-purple-500" /> Lookout For
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
                      {selectedStartup.lookout_for.map((item: string, i: number) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-purple-500">•</span> {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
            
          </div>
        </DialogContent></Dialog>

      </main>
    </div>
  )
}

function capitalizeFirst(string: string) {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase().replace(/_/g, ' ');
}
